import { useState, useRef, useEffect } from "react";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { TourStep } from "./useTourGuide";

/**
 * Hook to get hover tooltip configuration for a specific step
 * Returns hover handlers and tooltip data if hover tooltips are enabled
 */
export const useTourGuideHover = (
  stepId: string,
  stepData: TourStep | null
) => {
  const { hoverTooltipsEnabled, setHoveredStepId, disabledHoverSteps } =
    useTourGuideContext();
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if this specific step is disabled
  const isStepDisabled = disabledHoverSteps.has(stepId);

  const handleMouseEnter = () => {
    if (!hoverTooltipsEnabled || !stepData || isStepDisabled) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    setIsHovered(true);
    setHoveredStepId(stepId);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      setHoveredStepId(null);
    }, 100); // Small delay to prevent flickering
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return {
    isHovered:
      isHovered && hoverTooltipsEnabled && !isStepDisabled && stepData !== null,
    hoverProps: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
    tooltipData: stepData
      ? {
          title: stepData.title,
          description: stepData.description,
          position: stepData.position || "right",
          offset: stepData.offset || { x: 20, y: 0 },
        }
      : null,
  };
};
